import React, { useRef } from 'react'
import {
  Input,
  FieldGroup,
  Textarea,
  FormField,
  DesignToken,
  SpacingToken,
  Text,
} from '@livechat/design-system-react-components'
import { css } from '@emotion/react'
import { Form, Field } from 'react-final-form'
import uEmojiParser from 'universal-emoji-parser'

import mediaQueries from '@/layout/media-queries'
import { sendFeedback } from '@/store/feedback'
import { getItemById, getUserData } from '@/store/selectors'
import { required, minLength, maxLength, compose } from '@/utils/validators'
import { useTypedDispatch, useTypedSelector } from '@/store/hooks'
import useParamSelector from '@/hooks/useParamSelector'
import { APP_VALIDATORS, VALIDATION_ERROR } from '@/constants'
import VotingStars from '../VotingStars'

const sectionTitleCss = css`
  margin: 0;
`

const rateBlockCss = css`
  border-bottom: 1px solid var(${DesignToken.BorderBasicSecondary});

  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(${SpacingToken.Spacing5}) 0;
  }

  small {
    font-size: 12px;
  }
`

const formBlockCss = css`
  padding-top: var(${SpacingToken.Spacing5});

  > p {
    margin-bottom: 15px;
  }
`

const inputFieldGroupCss = css`
  column-gap: var(${SpacingToken.Spacing4});

  > div {
    width: 100%;
  }

  ${mediaQueries.xsmallScreen()} {
    flex-direction: row;
  }
`

const textAreaFieldCss = css`
  width: 100%;
`

const textareaCss = css`
  width: 100%;

  textarea {
    width: 100%;
    resize: none;
    min-height: 110px;

    ${mediaQueries.xsmallScreen()} {
      min-height: 96px;
    }
  }
`

const errorMessage = css`
  color: var(${DesignToken.ContentBasicNegative});
`

interface FormValues {
  rate?: number
  text?: string
  name?: string
  email?: string
}

interface Props {
  id: string
  rate: number | null
  onClose: () => void
}

const ReviewForm = ({ id, rate, onClose }: Props) => {
  const dispatch = useTypedDispatch()
  const productName = useParamSelector(getItemById, id)?.name || 'application'
  const { email, name: username } = useTypedSelector(getUserData)

  const { current: initialRate } = useRef(rate)

  const handleReview = (values: FormValues) => {
    const { text, ...rest } = values
    const parsedText = text ? uEmojiParser.parseToShortcode(text) : ''

    dispatch(sendFeedback.pending({ id, text: parsedText, ...rest }))
    onClose()
  }

  return (
    <Form
      onSubmit={handleReview}
      initialValues={{
        name: username,
        rate: initialRate,
        email: email,
      }}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} id="reviewForm">
          <div css={rateBlockCss} id="rating-modal-voting-stars">
            <Text css={sectionTitleCss} size="lg" bold>
              How would you rate your experience with {productName}?
            </Text>

            <div>
              <Field
                name="rate"
                validate={required(VALIDATION_ERROR.RATE)}
                render={({ input, meta }) => {
                  const setRate = (rate: number) =>
                    input.onChange({ target: { value: rate } })
                  const rate = Number(input.value)

                  return (
                    <>
                      <VotingStars rate={rate} setRate={setRate} size={35} />

                      <small>
                        {meta.error && meta.touched ? (
                          <span
                            css={errorMessage}
                            data-test="rating-modal-error-message"
                          >
                            This field cannot be blank, please rate the app on a
                            scale from 1-5
                          </span>
                        ) : (
                          <span>
                            Choose number of stars you would give on a scale
                            from 1–5
                          </span>
                        )}
                      </small>
                    </>
                  )
                }}
              />
            </div>
          </div>

          <div css={formBlockCss}>
            <Text css={sectionTitleCss} size="lg" bold>
              What do you think about {productName}?
            </Text>

            <Field
              name="text"
              validate={compose(
                required(VALIDATION_ERROR.MIN_LENGTH.REVIEW),
                minLength(
                  APP_VALIDATORS.MIN_LENGTH.REVIEW,
                  VALIDATION_ERROR.MIN_LENGTH.REVIEW
                ),
                maxLength(
                  APP_VALIDATORS.MAX_LENGTH.REVIEW,
                  VALIDATION_ERROR.MAX_LENGTH.REVIEW
                )
              )}
            >
              {({ input, meta }) => (
                <FormField
                  labelFor="your-review-form-textarea"
                  css={textAreaFieldCss}
                  labelText="Your review"
                  error={
                    meta.submitFailed &&
                    meta.error && (
                      <span data-test="field-error">{meta.error || ''}</span>
                    )
                  }
                >
                  <Textarea
                    css={textareaCss}
                    id="your-review-form-textarea"
                    placeholder="By sharing your experience, you are sending shout outs to the developer and helping others make a decision. If you are providing constructive feedback, please be kind."
                    error={!!(meta.submitFailed && meta.error)}
                    {...input}
                  />
                </FormField>
              )}
            </Field>

            <FieldGroup css={inputFieldGroupCss}>
              <Field
                name="name"
                validate={compose(
                  required(VALIDATION_ERROR.MIN_LENGTH.NAME),
                  minLength(
                    APP_VALIDATORS.MIN_LENGTH.NAME,
                    VALIDATION_ERROR.MIN_LENGTH.NAME
                  ),
                  maxLength(
                    APP_VALIDATORS.MAX_LENGTH.NAME,
                    VALIDATION_ERROR.MAX_LENGTH.NAME
                  )
                )}
              >
                {({ input, meta }) => (
                  <FormField
                    labelFor="rating-modal-name"
                    labelText="Your name"
                    description="Will be displayed next to your review"
                    error={
                      meta.submitFailed &&
                      meta.error && (
                        <span data-test="field-error">
                          {(meta.submitFailed && meta.error) || ''}
                        </span>
                      )
                    }
                  >
                    <Input
                      id="rating-modal-name"
                      error={!!(meta.submitFailed && meta.error)}
                      {...input}
                    />
                  </FormField>
                )}
              </Field>

              <Field name="email">
                {({ input }) => (
                  <FormField
                    labelFor="rating-modal-email"
                    labelText="Your e-mail"
                    description="Will be hidden for public"
                  >
                    <Input id="rating-modal-email" disabled {...input} />
                  </FormField>
                )}
              </Field>
            </FieldGroup>
          </div>
        </form>
      )}
    />
  )
}

export default ReviewForm
